import { useState, useContext, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { GlobalContext } from "../../../context/Context";
import { TableCard } from "../../../component/table/table-layout";
import {
  FeildWraper,
  FormArea,
  FormLabel,
  FeildError,
} from "../../../component/form/form-layout";
import { useApiCall } from "../../../component/custom-hooks/api-call";
import Select from "react-select";
import { Loader2 } from "lucide-react";
import { Alert, Snackbar } from "@mui/material";

const MyAccount = ({ Style }) => {
  const { state, dispatch } = useContext(GlobalContext);
  const apiCall = useApiCall();

  const [alertData, setAlertData] = useState({ isShow: false });

  const onSuccess = (message) => {
    setAlertData({ isShow: true, type: "success", message });
  };

  const onError = (message) => {
    setAlertData({ isShow: true, type: "error", message });
  };

  const handleClose = () => {
    setAlertData({ isShow: false });
  };

  const [activeTab, setActiveTab] = useState("personal");

  // location lists + loading
  const [countryList, setCountryList] = useState([]);
  const [stateList, setStateList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [loadingCountry, setLoadingCountry] = useState(false);
  const [loadingState, setLoadingState] = useState(false);
  const [loadingCity, setLoadingCity] = useState(false);

  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null);

  // fetch countries
  const getCountries = async () => {
    try {
      setLoadingCountry(true);
      const res = await apiCall({ method: "get", url: "location/get-countries" });
      setCountryList(res?.data || []);
    } catch (err) {
      console.warn("Country fetch error:", err);
    } finally {
      setLoadingCountry(false);
    }
  };

  const getStates = async (countryId) => {
    if (!countryId) return;
    try {
      setLoadingState(true);
      const res = await apiCall({
        method: "get",
        url: `location/get-states-by-country/${countryId}`,
      });
      setStateList(res?.data || []);
      setCityList([]);
    } catch (err) {
      console.warn("State fetch error:", err);
    } finally {
      setLoadingState(false);
    }
  };

  const getCities = async (stateId) => {
    if (!stateId) return;
    try {
      setLoadingCity(true);
      const res = await apiCall({
        method: "get",
        url: `location/get-cities-by-state/${stateId}`,
      });
      setCityList(res?.data || []);
    } catch (err) {
      console.warn("City fetch error:", err);
    } finally {
      setLoadingCity(false);
    }
  };

  // initial load countries
  useEffect(() => {
    getCountries();
  }, []);

  // read user data from context or localStorage
  const userData = state?.user || JSON.parse(localStorage.getItem("user") || "{}");

  // ---------- Formik: Profile (personal + address) ----------
  const profileValidation = Yup.object({
    name: Yup.string().required("Full name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    phone: Yup.string().nullable(),
    address: Yup.string().nullable(),
    city: Yup.string().nullable(),
    state: Yup.string().nullable(),
    zip: Yup.string().nullable(),
    country: Yup.string().nullable(),
  });

  const profileFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: userData?.name || "",
      email: userData?.email || "",
      role: userData?.role || "User",
      addedOn: userData?.created_at
        ? new Date(userData.created_at).toLocaleString()
        : "-",
      phone: userData?.phone || "",
      dob: userData?.dob || "",
      address: userData?.address || "",
      city: userData?.city || "",
      state: userData?.state || "",
      zip: userData?.zipcode || "",
      country: userData?.country || "",
      profileImg: userData?.userimg || "",
    },
    validationSchema: profileValidation,
    onSubmit: async (values, { setSubmitting, setFieldError }) => {
      try {
        // prepare payload - adjust keys as backend expects
        const payload = {
          name: values.name,
          email: values.email,
          phone: values.phone,
          dob: values.dob,
          address: values.address,
          city: values.city,
          state: values.state,
          zipcode: values.zip,
          country: values.country,
        };

        const res = await apiCall({
          method: "post",
          url: "update-my-profile",
          data: payload,
        });

        // Optionally update context & localStorage with new data
        const updatedUser = { ...userData, ...payload, userimg: values.profileImg };
        localStorage.setItem("user", JSON.stringify(updatedUser));
        dispatch && dispatch({ type: "USER_LOGIN", payload: updatedUser });

        onSuccess(res?.message || `Request Submitted`);
      } catch (err) {


        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.respMsg ||
          "Failed to update profile";
        // generic server error -> set top-level field error if you have UI for it
        onError(msg);
        setFieldError("general", msg);
      } finally {
        setSubmitting(false);
      }
    },
  });

  // ---------- Formik: Password ----------
  const passwordValidation = Yup.object({
    current: Yup.string().required("Current password is required"),
    newPass: Yup.string()
      .min(8, "New password must be at least 8 characters")
      .required("New password is required"),
    confirm: Yup.string()
      .oneOf([Yup.ref("newPass"), null], "Passwords must match")
      .required("Confirm password is required"),
  });

  const passwordFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      current: "",
      newPass: "",
      confirm: "",
    },
    validationSchema: passwordValidation,
    onSubmit: async (values, { setSubmitting, setFieldError, resetForm }) => {
      try {
        const payload = {
          current_password: values.current,
          new_password: values.newPass,
          new_password_confirmation: values.newPass,
        };

        const res = await apiCall({
          method: "post",
          url: "update-my-password",
          data: payload,
        });

        onSuccess(res?.message || `Password updated successfully`);
        resetForm();
      } catch (err) {
        console.error("Change password error:", err);
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.respMsg ||
          "Failed to change password";
        // If backend provides field-specific error, set it
        if (err?.response?.data?.field === "current_password") {
          setFieldError("current", msg);
        } else {
          setFieldError("general", msg);
        }

        onError(msg);
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Helper initials
  const getInitials = (name = "") => {
    if (!name) return "U";
    const parts = name.trim().split(" ").filter(Boolean);
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  // select styles (same as you used)
  const selectStyles = {
    control: (base, s) => ({
      ...base,
      minHeight: "35px",
      height: "auto",
      borderRadius: "5px",
      border: "1px solid #DFDFDF",
      backgroundColor: s.isDisabled ? "#f0f0f0" : "#FFF",
      boxShadow: "none",
      "&:hover": { borderColor: "#DFDFDF" },
      fontSize: "13px",
      lineHeight: "1.2",
      fontWeight: 500,
      color: "#1E1E1E",
      padding: "0 14px",
      cursor: s.isDisabled ? "not-allowed" : "normal",
    }),
    valueContainer: (base) => ({ ...base, height: "auto", padding: "0 0px 0" }),
    input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: "14px", fontWeight: 500 }),
    singleValue: (base) => ({ ...base, fontSize: "14px", fontWeight: 500 }),
    placeholder: (base) => ({ ...base, fontSize: "14px", fontWeight: 500, color: "#717680" }),
    menuPortal: (base) => ({ ...base, zIndex: 99999 }),
    menu: (base) => ({ ...base, marginTop: "2px", borderRadius: "5px", border: "1px solid #DFDFDF", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }),
    option: (base, s) => ({
      ...base,
      fontSize: "14px",
      fontWeight: 500,
      color: s.isSelected ? "#FFF" : "#1E1E1E",
      backgroundColor: s.isSelected ? "#3b82f6" : s.isFocused ? "#F3F4F6" : "#FFF",
      padding: "8px 14px",
      cursor: "pointer",
    }),
    indicatorContainer: (base) => ({ ...base, padding: 0 }),
    dropdownIndicator: (base) => ({ ...base, padding: 0 }),
  };

  // --- Auto load states/cities when initial data exists ---
  useEffect(() => {
    // once countries list available and profileFormik has a country name, pick corresponding country id and load states
    if (countryList.length && profileFormik.values.country) {
      const matched = countryList.find((c) => c.country_name === profileFormik.values.country);
      if (matched) {
        setSelectedCountry(matched.id);
        getStates(matched.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryList]);

  useEffect(() => {
    // when stateList arrives and profileFormik has state, find and load cities
    if (stateList.length && profileFormik.values.state) {
      const matched = stateList.find((s) => s.name === profileFormik.values.state);
      if (matched) {
        setSelectedState(matched.id);
        getCities(matched.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateList]);

  // helpers to map options
  const countryOptions = countryList.map((c) => ({ value: c.id, label: c.country_name }));
  const stateOptions = stateList.map((s) => ({ value: s.id, label: s.name }));
  const cityOptions = cityList.map((ct) => ({ value: ct.id, label: ct.name }));

  return (
    <>
      <TableCard className={'overflow-hidden'}>
        <div className="rounded-2xl overflow-hidden bg-white border border-gray-200 shadow-sm">
          {/* Header */}
          {/* <div className="flex flex-col sm:flex-row items-center justify-between gap-6 p-8 border-b border-gray-100 bg-gray-50">
            <div className="flex items-center gap-5">
              <div className="relative w-24 h-24 rounded-full overflow-hidden border border-gray-300 bg-gray-100 flex items-center justify-center text-gray-600 text-2xl font-semibold">
                {profileFormik.values.profileImg ? (
                  <img
                    src={profileFormik.values.profileImg}
                    alt="Profile"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.style.display = "none";
                      e.currentTarget.parentNode.innerHTML = getInitials(profileFormik.values.name);
                    }}
                  />
                ) : (
                  getInitials(profileFormik.values.name)
                )}
              </div>

              <div>
                <h2 className="text-xl font-semibold text-gray-800 leading-tight">
                  {profileFormik.values.name}
                </h2>
                <p className="text-sm text-gray-500">{profileFormik.values.email}</p>
                <p className="text-sm text-gray-500 mt-1">
                  Role: <span className="font-medium text-gray-700">{profileFormik.values.role}</span>
                </p>
              </div>
            </div>
          </div> */}

          {/* Tabs */}
          {/* <div className="flex border-b border-gray-200 bg-white">
            <button
              onClick={() => setActiveTab("personal")}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-all ${activeTab === "personal"
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
            >
              Personal Information
            </button>

            <button
              onClick={() => setActiveTab("password")}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-all ${activeTab === "password"
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
            >
              Password & Login
            </button>
          </div> */}

          {/* Tab content */}
          {/* {activeTab === "personal" ? ( */}
            <form onSubmit={profileFormik.handleSubmit} className="p-8 space-y-10">
              {/* User Info */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-6">User Information</h3>
                <FormArea columns={2}>
                  <FeildWraper>
                    <FormLabel>Full Name</FormLabel>
                    <input
                      type="text"
                      name="name"
                      value={profileFormik.values.name}
                      onChange={profileFormik.handleChange}
                      onBlur={profileFormik.handleBlur}

                    />
                    {profileFormik.touched.name && profileFormik.errors.name && (
                      <FeildError>{profileFormik.errors.name}</FeildError>
                    )}
                  </FeildWraper>

                  <FeildWraper>
                    <FormLabel>Email</FormLabel>
                    <input
                      type="email"
                      name="email"
                      value={profileFormik.values.email}
                      onChange={profileFormik.handleChange}
                      onBlur={profileFormik.handleBlur}
                      disabled
                      className="w-full bg-gray-100 border border-gray-300 rounded-lg px-3 py-2 text-gray-600 cursor-not-allowed"
                    />
                    {profileFormik.touched.email && profileFormik.errors.email && (
                      <FeildError>{profileFormik.errors.email}</FeildError>
                    )}
                  </FeildWraper>

                  <FeildWraper>
                    <FormLabel>Role</FormLabel>
                    <input
                      type="text"
                      name="role"
                      value={profileFormik.values.role}
                      readOnly
                      className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-gray-500 cursor-not-allowed"
                    />
                  </FeildWraper>

                  <FeildWraper>
                    <FormLabel>Added On</FormLabel>
                    <input
                      type="text"
                      name="addedOn"
                      value={profileFormik.values.addedOn}
                      readOnly
                      className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-gray-500 cursor-not-allowed"
                    />
                  </FeildWraper>

                  <FeildWraper>
                    <FormLabel>Phone</FormLabel>
                    <input
                      type="text"
                      name="phone"
                      value={profileFormik.values.phone}
                      onChange={profileFormik.handleChange}
                      onBlur={profileFormik.handleBlur}

                    />
                    {profileFormik.touched.phone && profileFormik.errors.phone && (
                      <FeildError>{profileFormik.errors.phone}</FeildError>
                    )}
                  </FeildWraper>

                  <FeildWraper>
                    <FormLabel>DOB</FormLabel>
                    <input
                      type="date"
                      name="dob"
                      value={profileFormik.values.dob || ""}
                      onChange={profileFormik.handleChange}
                      onBlur={profileFormik.handleBlur}

                    />
                  </FeildWraper>
                </FormArea>
              </div>

              {/* Address */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-6">Address Information</h3>

                <FormArea columns={2}>
                  <FeildWraper>
                    <FormLabel>Address</FormLabel>
                    <input
                      type="text"
                      name="address"
                      value={profileFormik.values.address}
                      onChange={profileFormik.handleChange}
                      onBlur={profileFormik.handleBlur}

                    />
                  </FeildWraper>

                  <FeildWraper>
                    <FormLabel>Country</FormLabel>
                    <Select
                      className="text-sm"
                      classNamePrefix="react-select"
                      value={
                        countryOptions.find((opt) => opt.label === profileFormik.values.country) || null
                      }
                      onChange={(option) => {
                        if (!option?.value) return;
                        profileFormik.setFieldValue("country", option.label);
                        profileFormik.setFieldValue("state", "");
                        profileFormik.setFieldValue("city", "");
                        setSelectedCountry(option.value);
                        setSelectedState(null);
                        getStates(option.value);
                      }}
                      options={countryOptions}
                      isLoading={loadingCountry}
                      isClearable
                      placeholder="Select country..."
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      components={{
                        IndicatorSeparator: () => null,
                        LoadingIndicator: () => (
                          <div className="flex items-center pr-2">
                            <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                          </div>
                        ),
                      }}
                      styles={selectStyles}
                    />
                  </FeildWraper>

                  <FeildWraper>
                    <FormLabel>State</FormLabel>
                    <Select
                      className="text-sm"
                      classNamePrefix="react-select"
                      value={stateOptions.find((opt) => opt.label === profileFormik.values.state) || null}
                      onChange={(option) => {
                        profileFormik.setFieldValue("state", option?.label || "");
                        profileFormik.setFieldValue("city", "");
                        setSelectedState(option?.value);
                        getCities(option?.value);
                      }}
                      options={stateOptions}
                      isLoading={loadingState}
                      isDisabled={!selectedCountry}
                      isClearable
                      placeholder="Select state..."
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      components={{
                        IndicatorSeparator: () => null,
                        LoadingIndicator: () => (
                          <div className="flex items-center pr-2">
                            <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                          </div>
                        ),
                      }}
                      styles={selectStyles}
                    />
                  </FeildWraper>

                  <FeildWraper>
                    <FormLabel>City</FormLabel>
                    <Select
                      className="text-sm"
                      classNamePrefix="react-select"
                      value={cityOptions.find((opt) => opt.label === profileFormik.values.city) || null}
                      onChange={(option) => profileFormik.setFieldValue("city", option?.label || "")}
                      options={cityOptions}
                      isLoading={loadingCity}
                      isDisabled={!selectedState}
                      isClearable
                      placeholder="Select city..."
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      components={{
                        IndicatorSeparator: () => null,
                        LoadingIndicator: () => (
                          <div className="flex items-center pr-2">
                            <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                          </div>
                        ),
                      }}
                      styles={selectStyles}
                    />
                  </FeildWraper>

                  <FeildWraper>
                    <FormLabel>Zip Code</FormLabel>
                    <input
                      type="text"
                      name="zip"
                      value={profileFormik.values.zip}
                      onChange={profileFormik.handleChange}
                      onBlur={profileFormik.handleBlur}

                    />
                  </FeildWraper>
                </FormArea>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={profileFormik.isSubmitting}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg transition-all font-medium"
                >
                  {profileFormik.isSubmitting ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          {/* ) : ( */}
            {/* <form onSubmit={passwordFormik.handleSubmit} className="p-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-6">Change Password</h3>

              <FormArea columns={3}>
                <FeildWraper>
                  <FormLabel>Current Password</FormLabel>
                  <input
                    type="password"
                    name="current"
                    value={passwordFormik.values.current}
                    onChange={passwordFormik.handleChange}
                    onBlur={passwordFormik.handleBlur}
                    placeholder="Enter current password"

                  />
                  {passwordFormik.touched.current && passwordFormik.errors.current && (
                    <FeildError>{passwordFormik.errors.current}</FeildError>
                  )}
                </FeildWraper>

                <FeildWraper>
                  <FormLabel>New Password</FormLabel>
                  <input
                    type="password"
                    name="newPass"
                    value={passwordFormik.values.newPass}
                    onChange={passwordFormik.handleChange}
                    onBlur={passwordFormik.handleBlur}
                    placeholder="Enter new password"

                  />
                  {passwordFormik.touched.newPass && passwordFormik.errors.newPass && (
                    <FeildError>{passwordFormik.errors.newPass}</FeildError>
                  )}
                </FeildWraper>

                <FeildWraper width="100%">
                  <FormLabel>Confirm Password</FormLabel>
                  <input
                    type="password"
                    name="confirm"
                    value={passwordFormik.values.confirm}
                    onChange={passwordFormik.handleChange}
                    onBlur={passwordFormik.handleBlur}
                    placeholder="Confirm new password"

                  />
                  {passwordFormik.touched.confirm && passwordFormik.errors.confirm && (
                    <FeildError>{passwordFormik.errors.confirm}</FeildError>
                  )}
                </FeildWraper>
              </FormArea>

              <div className="flex justify-end mt-6">
                <button
                  type="submit"
                  disabled={passwordFormik.isSubmitting}
                  className="bg-gray-800 hover:bg-gray-900 text-white px-5 py-2.5 rounded-lg transition-all font-medium"
                >
                  {passwordFormik.isSubmitting ? "Updating..." : "Update Password"}
                </button>
              </div>
            </form> */}
          {/* )} */}
        </div>
      </TableCard>



      <Snackbar
        open={alertData?.isShow}
        autoHideDuration={3000}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          severity={alertData?.type}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {alertData?.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default MyAccount;
